"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@/shared/ui/buttons/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/ui/data-display/avatar";
import { ApplyButton } from "./apply-button";
import { WriteAboutPostButton } from "./write-dialog";
import type { LfgLftPostResponse } from "@/shared/api/lfg-lft-types";
import { cn } from "@/shared/lib/utils";
import { isLfgRole, toLfgRank, type LfgRole } from "../model/board-filters";
import { CardChip, LfgRoleIcon, MicIcon, RankLabel } from "./board-card-parts";
import { BoardRequirements } from "./board-requirements";
import { BoardTeamRoster } from "./board-team-roster";
import { BoardInviteDialog, type InviteTeamOption } from "./board-invite-dialog";

/** Отклик возможен только на линию: заявка на бэке хранит GameRole (Top…Support). */
const LANES: readonly LfgRole[] = ["Top", "Jungle", "Mid", "Adc", "Support"];

const PRIMARY_BTN =
  "h-[34px] border-highlight-border bg-highlight-soft px-3.5 font-bold text-highlight hover:bg-highlight-soft/80 hover:text-highlight";

export interface BoardPostCardProps {
  post: LfgLftPostResponse;
  tzOffset: number;
  isAuthor: boolean;
  /** Объявление моей команды, где я не управляю: ни отклика, ни «Написать». */
  isOwnTeam?: boolean;
  /** Уже есть активный отклик/приглашение этого пользователя на объявление. */
  applied: boolean;
  inviteTeams: InviteTeamOption[];
  actionsDisabled: boolean;
  onApply: (role: LfgRole) => void;
  onInvite: (teamId: string, message: string) => void;
  onClose: () => void;
  /** Нет — кнопки «Изменить» нет: форма редактирования приходит с модалкой создания (KAN-1354). */
  onEdit?: () => void;
  /** Командное объявление своей команды: собрать состав, позвав друзей на свободные позиции. */
  onBuild?: () => void;
}

export function BoardPostCard(p: BoardPostCardProps) {
  const t = useTranslations("LfgBoard");
  const { post } = p;
  const isTeam = post.type === "Lft";
  const [inviteOpen, setInviteOpen] = useState(false);

  const ps = post.playerSurface;
  const riot = ps?.primaryRiot;
  const name = (isTeam ? post.author?.displayName : ps?.displayName ?? post.author?.displayName) ?? "—";
  const avatar = (isTeam ? post.author?.avatarUrl : ps?.avatarUrl ?? post.author?.avatarUrl) ?? undefined;
  const tag = isTeam ? post.author?.tag : riot ? riot.tagLine : null;

  const roles = (post.roles ?? [post.role]).filter(isLfgRole);
  const secondary = post.secondaryRole && isLfgRole(post.secondaryRole) ? post.secondaryRole : null;
  const primary = roles.find((r) => r !== secondary) ?? null;
  const soughtLanes = LANES.filter((r) => roles.includes(r));
  const captain = post.rosterSlots.find((s) => s.isCaptain);
  const writeTo = isTeam ? captain?.userId : post.author?.id;

  return (
    <article className="flex flex-wrap items-stretch gap-5 rounded-[14px] border border-border bg-card p-4">
      <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-3">
        <header className="flex items-center gap-3">
          <Avatar className="size-11 flex-none">
            <AvatarImage src={avatar} alt="" />
            <AvatarFallback className="font-logo text-[15px]">{name.slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate font-logo text-[17px]">{name}</h2>
              {tag && <span className="text-xs text-muted-foreground">#{tag}</span>}
              <span
                className={cn(
                  "inline-flex h-[22px] items-center whitespace-nowrap rounded-md px-2 text-[11px] font-bold",
                  isTeam ? "bg-muted text-muted-foreground" : "bg-highlight-soft text-highlight",
                )}
              >
                {isTeam ? t("card.kindTeam") : t("card.kindPlayer")}
              </span>
            </div>
            {isTeam && post.author?.description && (
              <p className="truncate text-xs text-muted-foreground">{post.author.description}</p>
            )}
          </div>
        </header>

        {!isTeam && (
          <div className="flex flex-wrap items-center gap-1.5">
            {riot ? (
              <CardChip size="md">
                <RankLabel rank={toLfgRank(riot.currentRank)} size={15} />
              </CardChip>
            ) : (
              <CardChip size="md" className="text-muted-foreground">{t("card.noRiot")}</CardChip>
            )}
            {primary && (
              <CardChip size="md">
                <LfgRoleIcon role={primary} size={15} />
                {t(`role.${primary}`)}
                {secondary && (
                  <>
                    <span aria-hidden className="text-muted-foreground">/</span>
                    <LfgRoleIcon role={secondary} size={15} />
                    {t(`role.${secondary}`)}
                  </>
                )}
              </CardChip>
            )}
            {post.mic === "With" && <CardChip size="md"><MicIcon mic="With" title={t("card.hasMic")} /></CardChip>}
            {post.mic === "Without" && <CardChip size="md"><MicIcon mic="Without" title={t("card.noMic")} /></CardChip>}
          </div>
        )}

        {post.description && (
          <p className="whitespace-pre-wrap text-sm font-semibold leading-normal text-foreground [text-wrap:pretty]">{post.description}</p>
        )}

        {isTeam && post.rosterSlots.length > 0 && <BoardTeamRoster slots={post.rosterSlots} />}
      </div>

      {/* На телефоне колонка требований уходит под карточку — разделитель сверху, а не слева. */}
      <div className="flex min-w-0 flex-[1_1_240px] flex-col gap-2.5 border-t border-border pt-4 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-5">
        <BoardRequirements post={post} tzOffset={p.tzOffset} />
        <div className="flex-1" />
        <div className="flex flex-wrap gap-2 pt-1">
          {p.isAuthor ? (
            <>
              {p.onBuild && <Button variant="outline" className={PRIMARY_BTN} onClick={p.onBuild}>{t("teamBuilder.open")}</Button>}
              {p.onEdit && <Button variant="outline" className="h-[34px]" onClick={p.onEdit}>{t("card.edit")}</Button>}
              <Button variant="outline" className="h-[34px] text-muted-foreground" onClick={p.onClose}>{t("card.close")}</Button>
            </>
          ) : p.isOwnTeam ? null : (
            <>
              {p.applied ? (
                <Button variant="outline" className="h-[34px] text-muted-foreground" disabled>{t("card.applied")}</Button>
              ) : isTeam ? (
                <ApplyButton lanes={soughtLanes} disabled={p.actionsDisabled} onApply={p.onApply} />
              ) : (
                <Button variant="outline" className={PRIMARY_BTN} disabled={p.actionsDisabled} onClick={() => setInviteOpen(true)}>
                  {t("card.invite")}
                </Button>
              )}
              <WriteAboutPostButton
                label={t("card.message")}
                target={{ postId: post.id, post, toUserId: writeTo, toName: name, kindLabel: isTeam ? t("card.kindTeam") : t("card.kindPlayer") }}
              />
            </>
          )}
        </div>
      </div>

      {!isTeam && (
        <BoardInviteDialog open={inviteOpen} onOpenChange={setInviteOpen} teams={p.inviteTeams} onInvite={p.onInvite} />
      )}
    </article>
  );
}

/** Одна искомая линия — сразу отклик; несколько — выбор, на какую. */
